import { usePriceFormat } from '@/hooks/usePriceFormat'
import { Button, HStack, Select, Stack } from '@chakra-ui/react'
import type { AdditionConstructionAssembly, OfferProperty, OfferPropertyAddition, PackageItemModel } from 'BrokerTsLib/src/dtos'
import { DurationKinds } from 'BrokerTsLib/src/dtos'
import type { UseFieldArrayRemove } from 'BrokerTsLib/src/rhf'
import { useFieldArray, useFormContext } from 'BrokerTsLib/src/rhf'
import { useEffect } from 'react'

type ConstructionAssemblyProps = {
  offer: OfferProperty
  addon: PackageItemModel
}


const getDefaultItem = (addon: PackageItemModel) => {
  const defaultVariant = addon.variants.find(x => x.isDefault) ?? addon.variants[0]

  return {
    packageItemId: addon.packageItemId,
    variantId: defaultVariant?.id,
    limit: defaultVariant?.defaultLimit?.toString(),
    riskId: addon.riskId,
  } as AdditionConstructionAssembly
}

function ConstructionAssembly({
  offer,
  addon
}: ConstructionAssemblyProps) {

  const {
    control,
    setValue
  } = useFormContext<OfferPropertyAddition>()

  const name = 'constructionAssemblies'

  const { fields, remove, append } = useFieldArray({
    control: control,
    name: name
  })

  // vychozi nastaveni hodnot na OfferPropertyAddition z OfferPart
  useEffect(() => {
    fields.forEach((item, index) => {
      const part = offer.parts.find(x => x.partId == item.offerPartId)
      if (!part) return

      setValue(`${name}.${index}.riskId`, part.packageItem.riskId)
      setValue(`${name}.${index}.limit`, part.limit?.toString())
      setValue(`${name}.${index}.variantId`, part.packageItemVariantId)
      setValue(`${name}.${index}.offerPartId`, part.partId)
      setValue(`${name}.${index}.packageItemId`, part.packageItem.packageItemId)
    })

    if (fields.length == 0)
      append(getDefaultItem(addon))
  }, [])

  return (
    <HStack gap={8}>
      {fields?.map((item, index) => {
        return <ConstructionAssemblyItem
          key={`${item.offerPartId}_${index}`}
          addon={addon}
          index={index}
          item={item}
          remove={remove}
          offer={offer}
        />
      })}
    </HStack >
  )
}

export default ConstructionAssembly

type ConstructionAssemblyItemProps = {
  item: AdditionConstructionAssembly,
  index: number,
  addon: PackageItemModel,
  remove: UseFieldArrayRemove
  offer: OfferProperty
}

const ConstructionAssemblyItem = ({
  item,
  index,
  addon,
  remove
}: ConstructionAssemblyItemProps) => {

  const name = 'constructionAssemblies'
  const { priceFormat } = usePriceFormat()

  const {
    register,
    setValue } = useFormContext<OfferPropertyAddition>()

  const handleChangeVariant = (limit: number, variantId?: number) => {
    setValue(`${name}.${index}.limit`, limit?.toString())
    setValue(`${name}.${index}.variantId`, variantId)
  }

  return (
    <Stack key={`${item.offerPartId}_${index}`} alignSelf='start' minW='120px'>
      <Select
        size='sm'
        sx={{ width: '100%' }}
        {...register(`${name}.${index}.limit`)}
        onChange={(e) => {
          const dataset = e.target.options[e.target.selectedIndex].dataset
          handleChangeVariant(parseInt(e.target.value), parseInt(dataset.id!))
        }}
      >
        {addon.variants.map(x => (
          <option key={`${x.id}_${x.defaultLimit}`} value={x.defaultLimit?.toString()} data-id={x.id}>
            {priceFormat(x.defaultLimit)} {x.isDefault ? '(výchozí)' : ''}
          </option>
        ))}
      </Select>

      <Select
        size='sm'
        sx={{ width: '100%' }}
        {...register(`${name}.${index}.duration`)}
      >
        {Object.keys(DurationKinds).map(key => {
          const value = DurationKinds[key as keyof typeof DurationKinds]
          return (
            <option key={key} value={key}>
              {value}
            </option>
          )
        })}
      </Select>
      <Button
        size='sm'
        colorScheme='secondary'
        variant='ghost'
        onClick={() => remove(index)}
      >
        Odebrat
      </Button>
    </Stack>
  )
}